import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Drawer,
    Empty,
    Input,
    Row,
    Segmented,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    EyeInvisibleOutlined,
    EyeOutlined,
    EditOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import { tmApi } from '../utils/taskManager';
import './TaskManagerAccessConstructor.css';

const { Title, Text } = Typography;

const ACCESS_OPTS = [
    {
        value: 'none',
        label: 'Скрыто',
        icon: <EyeInvisibleOutlined />
    },
    {
        value: 'read',
        label: 'Видеть',
        icon: <EyeOutlined />
    },
    {
        value: 'edit',
        label: 'Править',
        icon: <EditOutlined />
    }
];

const TaskManagerAccessConstructorPage = () => {
    const [loading, setLoading] = useState(false);
    const [savingKey, setSavingKey] = useState(null);
    const [meta, setMeta] = useState(null);
    const [targetMode, setTargetMode] = useState('field');
    const [roleId, setRoleId] = useState(null);
    const [workType, setWorkType] = useState('');
    const [stageName, setStageName] = useState('');
    const [creatorOrgId, setCreatorOrgId] = useState(null);
    const [executorOrgId, setExecutorOrgId] = useState(null);
    const [search, setSearch] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await tmApi.get(
                '/api/task-manager/access-constructor'
            );
            const payload = data.data || {};
            setMeta(payload);
            setRoleId(prev => {
                if (prev) return prev;
                return payload.roles?.[0]?.id || null;
            });
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки конструктора прав'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const stageOptions = useMemo(() => {
        const byWt = meta?.work_type_stages || {};
        if (workType && byWt[workType]?.length) {
            return byWt[workType].map(s => ({
                value: s.status_name,
                label: s.human_name || s.status_name
            }));
        }
        return (meta?.statuses || []).map(s => ({
            value: s.name,
            label: s.name
        }));
    }, [meta, workType]);

    const creatorOrgOptions = useMemo(
        () =>
            (meta?.organizations || [])
                .filter(o => o.org_type === 'creator')
                .map(o => ({
                    value: o.id,
                    label: o.human_name
                })),
        [meta]
    );

    const executorOrgOptions = useMemo(
        () =>
            (meta?.organizations || [])
                .filter(o => o.org_type === 'executor')
                .map(o => ({
                    value: o.id,
                    label: o.human_name
                })),
        [meta]
    );

    const contextMap = useMemo(() => {
        const map = {};
        (meta?.rules || []).forEach(rule => {
            if (Number(rule.role_id) !== Number(roleId)) return;
            if (String(rule.work_type || '') !== String(workType || '')) {
                return;
            }
            if (
                String(rule.stage_name || '') !==
                String(stageName || '')
            ) {
                return;
            }
            const ruleCreator = Number(rule.creator_org_id) || 0;
            const ruleExecutor = Number(rule.executor_org_id) || 0;
            if (
                ruleCreator !== (Number(creatorOrgId) || 0) ||
                ruleExecutor !== (Number(executorOrgId) || 0)
            ) {
                return;
            }
            map[`${rule.target_type}:${rule.target_id}`] =
                rule.access_level;
        });
        return map;
    }, [
        meta,
        roleId,
        workType,
        stageName,
        creatorOrgId,
        executorOrgId
    ]);

    const groupsById = useMemo(() => {
        const map = {};
        (meta?.groups || []).forEach(g => {
            map[g.id] = g;
        });
        return map;
    }, [meta]);

    const items = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (targetMode === 'group') {
            return (meta?.groups || [])
                .filter(g => {
                    if (!q) return true;
                    return (
                        String(g.human_name)
                            .toLowerCase()
                            .includes(q) ||
                        String(g.system_name)
                            .toLowerCase()
                            .includes(q)
                    );
                })
                .map(g => ({
                    key: `group:${g.id}`,
                    target_type: 'group',
                    target_id: g.id,
                    title: g.human_name,
                    subtitle: g.system_name,
                    section: 'Группы полей'
                }));
        }

        const fields = (meta?.fields || []).filter(f => {
            if (!q) return true;
            return (
                String(f.human_name).toLowerCase().includes(q) ||
                String(f.system_name).toLowerCase().includes(q)
            );
        });

        const sections = new Map();
        fields.forEach(f => {
            const group = f.group_id
                ? groupsById[f.group_id]
                : null;
            const section = group?.human_name || 'Без группы';
            if (!sections.has(section)) {
                sections.set(section, []);
            }
            sections.get(section).push({
                key: `field:${f.id}`,
                target_type: 'field',
                target_id: f.id,
                title: f.human_name,
                subtitle: `${f.system_name}${
                    f.field_kind === 'main' ? ' · основное' : ''
                }`,
                section,
                sort: f.sort_order || 0
            });
        });

        const rows = [];
        [...sections.entries()]
            .sort((a, b) => a[0].localeCompare(b[0], 'ru'))
            .forEach(([, list]) => {
                list.sort((a, b) => a.sort - b.sort);
                rows.push(...list);
            });
        return rows;
    }, [meta, targetMode, search, groupsById]);

    const setAccess = async (item, accessLevel) => {
        if (!roleId) {
            message.warning('Сначала выберите роль');
            return;
        }
        const prev =
            contextMap[`${item.target_type}:${item.target_id}`] ||
            'none';
        if (prev === accessLevel) return;

        setSavingKey(item.key);
        try {
            await tmApi.put('/api/task-manager/access-rules', {
                target_type: item.target_type,
                target_id: item.target_id,
                role_id: roleId,
                work_type: workType || '',
                stage_name: stageName || '',
                creator_org_id: creatorOrgId || 0,
                executor_org_id: executorOrgId || 0,
                access_level: accessLevel
            });
            setMeta(current => {
                if (!current) return current;
                const rules = [...(current.rules || [])];
                const idx = rules.findIndex(
                    r =>
                        r.target_type === item.target_type &&
                        Number(r.target_id) ===
                            Number(item.target_id) &&
                        Number(r.role_id) === Number(roleId) &&
                        String(r.work_type || '') ===
                            String(workType || '') &&
                        String(r.stage_name || '') ===
                            String(stageName || '') &&
                        (Number(r.creator_org_id) || 0) ===
                            (Number(creatorOrgId) || 0) &&
                        (Number(r.executor_org_id) || 0) ===
                            (Number(executorOrgId) || 0)
                );
                if (idx >= 0) {
                    rules[idx] = {
                        ...rules[idx],
                        access_level: accessLevel
                    };
                } else {
                    rules.push({
                        id: `tmp-${Date.now()}-${item.key}`,
                        target_type: item.target_type,
                        target_id: item.target_id,
                        role_id: roleId,
                        work_type: workType || '',
                        stage_name: stageName || '',
                        creator_org_id: creatorOrgId || 0,
                        executor_org_id: executorOrgId || 0,
                        access_level: accessLevel
                    });
                }
                return { ...current, rules };
            });
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Не удалось сохранить'
            );
            await load();
        } finally {
            setSavingKey(null);
        }
    };

    const bulkSet = async accessLevel => {
        if (!roleId || !items.length) return;
        setSavingKey('bulk');
        try {
            await tmApi.put('/api/task-manager/access-rules/bulk', {
                rules: items.map(item => ({
                    target_type: item.target_type,
                    target_id: item.target_id,
                    role_id: roleId,
                    work_type: workType || '',
                    stage_name: stageName || '',
                    creator_org_id: creatorOrgId || 0,
                    executor_org_id: executorOrgId || 0,
                    access_level: accessLevel
                }))
            });
            message.success(
                `Обновлено: ${items.length} ${
                    targetMode === 'group' ? 'групп' : 'полей'
                }`
            );
            await load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка массового сохранения'
            );
        } finally {
            setSavingKey(null);
        }
    };

    const roleName =
        (meta?.roles || []).find(r => Number(r.id) === Number(roleId))
            ?.name || '—';

    const creatorOrgName =
        creatorOrgOptions.find(
            o => Number(o.value) === Number(creatorOrgId)
        )?.label || null;
    const executorOrgName =
        executorOrgOptions.find(
            o => Number(o.value) === Number(executorOrgId)
        )?.label || null;

    const contextLabel = [
        roleName,
        workType || 'все виды работ',
        stageName || 'все этапы',
        creatorOrgName || 'любой создатель',
        executorOrgName || 'любой исполнитель'
    ].join(' · ');

    const ruleRowsForSelected = useMemo(() => {
        if (!selectedItem) return [];
        const roleById = new Map(
            (meta?.roles || []).map(role => [Number(role.id), role.name])
        );
        const creatorById = new Map(
            (meta?.organizations || [])
                .filter(org => org.org_type === 'creator')
                .map(org => [Number(org.id), org.human_name])
        );
        const executorById = new Map(
            (meta?.organizations || [])
                .filter(org => org.org_type === 'executor')
                .map(org => [Number(org.id), org.human_name])
        );
        return (meta?.rules || [])
            .filter(
                rule =>
                    rule.target_type === selectedItem.target_type &&
                    Number(rule.target_id) === Number(selectedItem.target_id)
            )
            .map(rule => ({
                key: rule.id,
                role_name:
                    roleById.get(Number(rule.role_id)) ||
                    `#${rule.role_id}`,
                work_type: rule.work_type || 'Все',
                stage_name: rule.stage_name || 'Все',
                creator_org:
                    Number(rule.creator_org_id) > 0
                        ? creatorById.get(Number(rule.creator_org_id)) ||
                          `#${rule.creator_org_id}`
                        : 'Любая',
                executor_org:
                    Number(rule.executor_org_id) > 0
                        ? executorById.get(Number(rule.executor_org_id)) ||
                          `#${rule.executor_org_id}`
                        : 'Любая',
                access_level: rule.access_level || 'none'
            }))
            .sort((a, b) => {
                if (a.role_name !== b.role_name) {
                    return a.role_name.localeCompare(b.role_name, 'ru');
                }
                if (a.work_type !== b.work_type) {
                    return a.work_type.localeCompare(b.work_type, 'ru');
                }
                return a.stage_name.localeCompare(b.stage_name, 'ru');
            });
    }, [meta, selectedItem]);

    let lastSection = null;

    return (
        <Layout>
            <div className="tm-access-page">
                <div className="tm-access-head">
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Конструктор прав
                        </Title>
                        <Text type="secondary">
                            Выберите контекст — и сразу настройте
                            права кликом по каждому полю
                        </Text>
                    </div>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={load}
                        loading={loading}
                    >
                        Обновить
                    </Button>
                </div>

                <Card className="tm-access-context" size="small">
                    <div className="tm-access-context-title">
                        Для кого настраиваем
                    </div>
                    <Row gutter={[12, 12]}>
                        <Col xs={24} md={8}>
                            <div className="tm-access-label">
                                Роль
                            </div>
                            <Select
                                style={{ width: '100%' }}
                                size="large"
                                value={roleId}
                                onChange={setRoleId}
                                options={(meta?.roles || []).map(
                                    r => ({
                                        value: r.id,
                                        label: r.name
                                    })
                                )}
                                placeholder="Выберите роль"
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <div className="tm-access-label">
                                Вид работ
                            </div>
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                size="large"
                                value={workType || undefined}
                                placeholder="Все виды работ"
                                onChange={v => {
                                    setWorkType(v || '');
                                    setStageName('');
                                }}
                                options={(
                                    meta?.work_types || []
                                ).map(w => ({
                                    value: w.value,
                                    label: w.label || w.value
                                }))}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <div className="tm-access-label">
                                Этап
                            </div>
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                size="large"
                                value={stageName || undefined}
                                placeholder="Все этапы"
                                onChange={v =>
                                    setStageName(v || '')
                                }
                                options={stageOptions}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <div className="tm-access-label">
                                Орг. создатель
                            </div>
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                size="large"
                                value={creatorOrgId || undefined}
                                placeholder="Любая"
                                onChange={v =>
                                    setCreatorOrgId(v || null)
                                }
                                options={creatorOrgOptions}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <div className="tm-access-label">
                                Орг. исполнитель
                            </div>
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                size="large"
                                value={executorOrgId || undefined}
                                placeholder="Любая"
                                onChange={v =>
                                    setExecutorOrgId(v || null)
                                }
                                options={executorOrgOptions}
                            />
                        </Col>
                    </Row>
                    <Alert
                        className="tm-access-hint"
                        type="info"
                        showIcon
                        message={
                            <>
                                Контекст:{' '}
                                <Text strong>{contextLabel}</Text>
                                . Пустой вид, этап или организация =
                                правило действует шире. Более узкое
                                правило важнее общего.
                            </>
                        }
                    />
                </Card>

                <Card size="small" className="tm-access-toolbar">
                    <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space wrap>
                            <Segmented
                                value={targetMode}
                                onChange={setTargetMode}
                                options={[
                                    {
                                        value: 'field',
                                        label: 'Поля'
                                    },
                                    {
                                        value: 'group',
                                        label: 'Группы'
                                    }
                                ]}
                            />
                            <Input
                                allowClear
                                placeholder="Поиск поля…"
                                style={{ width: 240 }}
                                value={search}
                                onChange={e =>
                                    setSearch(e.target.value)
                                }
                            />
                        </Space>
                        <Space wrap>
                            <Text type="secondary">Всем:</Text>
                            {ACCESS_OPTS.map(opt => (
                                <Button
                                    key={opt.value}
                                    size="small"
                                    disabled={!roleId || !!savingKey}
                                    icon={opt.icon}
                                    onClick={() =>
                                        bulkSet(opt.value)
                                    }
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </Space>
                    </Space>
                </Card>

                <Spin spinning={loading || savingKey === 'bulk'}>
                    {!roleId ? (
                        <Empty description="Выберите роль сверху" />
                    ) : !items.length ? (
                        <Empty description="Ничего не найдено" />
                    ) : (
                        <div className="tm-access-list">
                            {items.map(item => {
                                const showSection =
                                    targetMode === 'field' &&
                                    item.section !== lastSection;
                                if (showSection) {
                                    lastSection = item.section;
                                }
                                const value =
                                    contextMap[
                                        `${item.target_type}:${item.target_id}`
                                    ] || 'none';
                                return (
                                    <React.Fragment key={item.key}>
                                        {showSection ? (
                                            <div className="tm-access-section">
                                                {item.section}
                                            </div>
                                        ) : null}
                                        <div
                                            className={`tm-access-row tm-access-row--${value}${
                                                savingKey ===
                                                item.key
                                                    ? ' is-saving'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                setSelectedItem(item)
                                            }
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="tm-access-row-main">
                                                <div className="tm-access-row-title">
                                                    {item.title}
                                                </div>
                                                <div className="tm-access-row-sub">
                                                    {item.subtitle}
                                                </div>
                                            </div>
                                            <Segmented
                                                className="tm-access-seg"
                                                value={value}
                                                disabled={
                                                    !!savingKey
                                                }
                                                onClick={event =>
                                                    event.stopPropagation()
                                                }
                                                onChange={level =>
                                                    setAccess(
                                                        item,
                                                        level
                                                    )
                                                }
                                                options={ACCESS_OPTS.map(
                                                    opt => ({
                                                        value: opt.value,
                                                        label: (
                                                            <span className="tm-access-seg-label">
                                                                {
                                                                    opt.icon
                                                                }{' '}
                                                                {
                                                                    opt.label
                                                                }
                                                            </span>
                                                        )
                                                    })
                                                )}
                                            />
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}
                </Spin>
            </div>
            <Drawer
                title={
                    selectedItem
                        ? `Доступ: ${selectedItem.title}`
                        : 'Доступ к полю'
                }
                placement="right"
                width={760}
                open={!!selectedItem}
                onClose={() => setSelectedItem(null)}
            >
                {selectedItem ? (
                    <Space
                        direction="vertical"
                        size={12}
                        style={{ width: '100%' }}
                    >
                        <Text type="secondary">
                            {selectedItem.subtitle}
                        </Text>
                        {!ruleRowsForSelected.length ? (
                            <Empty description="Явных правил пока нет" />
                        ) : (
                            <Table
                                size="small"
                                pagination={{
                                    defaultPageSize: 12,
                                    showSizeChanger: true
                                }}
                                dataSource={ruleRowsForSelected}
                                columns={[
                                    {
                                        title: 'Роль',
                                        dataIndex: 'role_name',
                                        key: 'role_name',
                                        width: 170
                                    },
                                    {
                                        title: 'Вид работ',
                                        dataIndex: 'work_type',
                                        key: 'work_type',
                                        width: 130
                                    },
                                    {
                                        title: 'Этап',
                                        dataIndex: 'stage_name',
                                        key: 'stage_name',
                                        width: 150
                                    },
                                    {
                                        title: 'Орг. создатель',
                                        dataIndex: 'creator_org',
                                        key: 'creator_org',
                                        width: 150
                                    },
                                    {
                                        title: 'Орг. исполнитель',
                                        dataIndex: 'executor_org',
                                        key: 'executor_org',
                                        width: 160
                                    },
                                    {
                                        title: 'Доступ',
                                        dataIndex: 'access_level',
                                        key: 'access_level',
                                        width: 110,
                                        render: value => {
                                            if (value === 'edit') {
                                                return (
                                                    <Tag color="orange">
                                                        Править
                                                    </Tag>
                                                );
                                            }
                                            if (value === 'read') {
                                                return (
                                                    <Tag color="blue">
                                                        Видеть
                                                    </Tag>
                                                );
                                            }
                                            return (
                                                <Tag>
                                                    Скрыто
                                                </Tag>
                                            );
                                        }
                                    }
                                ]}
                                scroll={{ x: 940 }}
                            />
                        )}
                    </Space>
                ) : null}
            </Drawer>
        </Layout>
    );
};

export default TaskManagerAccessConstructorPage;
